import React, { useEffect, useReducer, useState } from 'react';
import { IOption } from 'components/Select/Select';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import { MultiDatePicker } from 'components/MyDatePicker/MultiDatePicker';
import Input from 'components/Input';
import Button from 'components/Button/Button';
import Icon from 'components/Icon/Icon';
import { Table, TD, TH, TR, ModalContainer } from '../PDPTerms/style';
import CoursePhaseList from './CoursePhaseList';
import Notify from 'components/Notify/Notify';
import { appDispatch } from 'store/store';
import { updateParticipants } from 'store/pdp/pdpSlice';
import { black, gray, green, red } from 'utils/style/colors';
import { CompanyPDPState, Participant, Topic } from 'store/pdp/types';
import { ICompany, ICompanyRole } from 'store/companies/companiesTypes';
import { ITags } from 'store/providers/providerTypes';
import { IUsers } from 'store/store';
import messages from './Messages';
import { useTranslation } from 'react-i18next';

interface IInitialState {
  courseId: number;
  isActiveCourse: boolean;
  courseTitle: string;
  courseGroupId: string;
  startTime: number;
  isMandatoryCourse: boolean;
  resetLoading: boolean;
  phasePropOverride?: Array<{
    phaseTagId: number;
    phaseStageId: number;
    startDate?: number;
    endDate?: number;
  }>;
}

interface IAction {
  type: 'update' | 'create' | 'reset';
  payload?: { courseId: number; data: Partial<IInitialState> };
}

const initialState: IInitialState[] = [];

const reducer = (state: IInitialState[], action: IAction): IInitialState[] => {
  switch (action.type) {
    case 'update': {
      const selectedCourse = state.find(
        (el) => el.courseId === action.payload?.courseId
      );
      if (!selectedCourse) return state;
      return [
        ...state.reduce((acc: IInitialState[], el) => {
          if (el.courseId === action.payload?.courseId) {
            acc.push({ ...el, ...action.payload?.data });
          } else {
            acc.push(el);
          }
          return acc;
        }, []),
      ];
    }
    case 'create':
      return [
        ...state,
        {
          courseId: action.payload?.courseId || 0,
          isActiveCourse: false,
          courseTitle: '',
          courseGroupId: '',
          startTime: 0,
          isMandatoryCourse: false,
          resetLoading: false,
          ...action.payload?.data,
        },
      ];
    case 'reset':
      return [];
    default:
      return state;
  }
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  termUserOptions: IOption[];
  pdpState: CompanyPDPState;
  company: ICompany;
  tags: ITags['data'];
  users: IUsers['map'];
  pdpId: number;
  termId: number;
  classesInTerm: any[];
}

const AddUserToCoursesModal: React.FC<Props> = ({
  isOpen,
  onClose,
  termUserOptions,
  pdpState,
  company,
  tags,
  pdpId,
  classesInTerm,
}) => {
  const { t } = useTranslation('companyManagement');
  const [selectedUserToCourse, setSelectedUserToCourse] = useState<IOption>();
  const [globalStartDate, setGlobalStartDate] = useState<number | null>(null);
  const [addUserToCourses, dispatchAddUserToCourses] = useReducer(
    reducer,
    initialState
  );
  const [loadingAddUserToCourses, setLoadingAddUserToCourses] =
    useState<boolean>(false);
  const [filterTagIds, setFilterTagIds] = useState([] as number[]);

  const selectedPdpId = pdpId;
  const tagOptions: { [key: number]: IOption[] } = {};
  company.pdpPanels
    .find((companyPdp) => companyPdp.id === selectedPdpId)!
    .properties.topicTags.forEach((it) => {
      tagOptions[it] = [];
    });

  Object.values(tags).forEach((it) => {
    if (
      it.parentId &&
      company.pdpPanels
        .find((companyPdp) => companyPdp.id === selectedPdpId)!
        .properties.topicTags.includes(it.parentId) &&
      !it.deleted
    ) {
      tagOptions[it.parentId].push({
        value: it.id,
        label: it.text,
      });
    }
  });

  const updateItemStartTime = (item: IInitialState, newTime: number) => {
    const coursePhaseProps =
      pdpState.classes![item.courseId].properties.phaseProps;
    let updatedOverrides: Array<{
      phaseTagId: number;
      phaseStageId: number;
      startDate?: number;
      endDate?: number;
    }> = [];

    if (newTime !== item.startTime) {
      const gapTime = newTime - item.startTime;
      updatedOverrides = coursePhaseProps
        .filter((phase) => !phase.disabled)
        .map((phase) => {
          const existingOverride = item.phasePropOverride?.find(
            (o) =>
              o.phaseTagId === phase.phaseTagId &&
              o.phaseStageId === phase.phaseStageId
          );
          const currentStartDate =
            existingOverride?.startDate ??
            phase.phaseStartDates?.[item.courseGroupId];
          const currentEndDate =
            existingOverride?.endDate ??
            phase.phaseEndDates?.[item.courseGroupId];
          return {
            phaseTagId: phase.phaseTagId,
            phaseStageId: phase.phaseStageId,
            startDate: currentStartDate
              ? currentStartDate + gapTime
              : undefined,
            endDate: currentEndDate ? currentEndDate + gapTime : undefined,
          };
        });
    }

    dispatchAddUserToCourses({
      type: 'update',
      payload: {
        courseId: item.courseId,
        data: {
          startTime: newTime,
          phasePropOverride: updatedOverrides,
        },
      },
    });
  };

  useEffect(() => {
    dispatchAddUserToCourses({ type: 'reset' });
    if (!selectedUserToCourse) return;

    Object.values(pdpState.topics || {}).forEach((topic: Topic) => {
      const userRole = Object.values(company.roles).reduce(
        (acc: number[], el: ICompanyRole) => {
          const hasUserRole = el.users.find(
            (user) => user.userId === selectedUserToCourse.value
          );
          if (hasUserRole && !acc.includes(el.id)) acc.push(el.id);
          return acc;
        },
        []
      );

      const hasSelectedUserRole = topic.properties.relatedToRoles.some(
        (roleId: number) => userRole.includes(roleId)
      );

      if (hasSelectedUserRole && classesInTerm) {
        const classTopic = classesInTerm.find(
          (it) => it.topicId === Number(topic.id) && !it.deleted
        );
        if (classTopic) {
          const participant =
            classTopic.participants?.[selectedUserToCourse?.value];
          const coursePhaseProps = classTopic.properties.phaseProps;
          const groupKey = classTopic.properties.classGroups[0]?.key;
          const overrides =
            participant?.participantProps?.phasePropOverride || [];

          const effectiveDates =
            coursePhaseProps
              ?.map((phase: any) => {
                const override = overrides.find(
                  (o: any) =>
                    o.phaseTagId === phase.phaseTagId &&
                    o.phaseStageId === phase.phaseStageId
                );
                return override?.startDate ?? phase.phaseStartDates?.[groupKey];
              })
              .filter(
                (date?: number | null) => date !== null && date !== undefined
              ) || [];

          const initialStartTime =
            effectiveDates.length > 0 ? Math.min(...effectiveDates) : 0;

          dispatchAddUserToCourses({
            type: 'create',
            payload: {
              courseId: Number(classTopic.id),
              data: {
                courseTitle: topic.properties.name,
                courseGroupId:
                  participant?.participantProps?.group ||
                  classTopic.properties.classGroups[0]?.key ||
                  '',
                startTime: globalStartDate || initialStartTime,
                phasePropOverride:
                  participant?.participantProps?.phasePropOverride || [],
                isMandatoryCourse: topic.properties.mandatoryForRoles.some(
                  (roleId: number) => userRole.includes(roleId)
                ),
                isActiveCourse: participant?.participantProps?.status
                  ? participant.participantProps.status !== 'Dropped'
                  : topic.properties.mandatoryForRoles.some((roleId: number) =>
                      userRole.includes(roleId)
                    ),
              },
            },
          });
        }
      }
    });
  }, [selectedUserToCourse]);

  const handleUpdateNewParticipant = (item: IInitialState) => {
    const currentParticipant =
      pdpState.classes?.[item.courseId]?.participants?.[
        selectedUserToCourse!.value
      ];
    const currentStatus = currentParticipant?.participantProps?.status;

    const newParticipant: Participant = {
      id: currentParticipant ? currentParticipant.id : undefined,
      userId: Number(selectedUserToCourse!.value),
      pdpClassId: item.courseId,
      participantProps: {
        status:
          currentStatus === 'Dropped' || !currentStatus
            ? ('Attended' as const)
            : currentStatus,
        group: item.courseGroupId,
        present: null,
        phasePropOverride: item.phasePropOverride || [],
      },
    };

    return newParticipant;
  };

  const handleAddAUserToCourses = async () => {
    setLoadingAddUserToCourses(true);
    const activeCourses = addUserToCourses.filter(
      (item: IInitialState) => item.isActiveCourse
    );
    const participantList = [];

    for (const item of activeCourses) {
      participantList.push(handleUpdateNewParticipant(item));
    }

    try {
      await appDispatch(
        updateParticipants({ pdpId, participants: participantList })
      );
      Notify(messages.savedSuccessfully);
    } catch (e: any) {
      if (e.message === 'user already a participant')
        Notify(messages.userAlreadyParticipant, 'error');
      else if (e.message === 'phase start date is after end date')
        Notify(messages.phaseStartDateIsAfterEndDate, 'error');
      else Notify(messages.errorSaving, 'error');
    } finally {
      setLoadingAddUserToCourses(false);
    }
  };

  const handleResetCustomTime = async (courseId: number) => {
    const item = addUserToCourses.find(
      (el: IInitialState) => el.courseId === courseId
    ) as IInitialState;
    if (item) {
      const result = confirm(messages.willBeReset);
      if (result) {
        const coursePhaseProps =
          pdpState.classes?.[courseId].properties.phaseProps;
        const newStartTime =
          coursePhaseProps?.reduce((acc: number, phase) => {
            if (
              phase.phaseStartDates?.[item.courseGroupId] &&
              phase.phaseStartDates[item.courseGroupId] < acc
            ) {
              acc = phase.phaseStartDates[item.courseGroupId];
            }
            return acc;
          }, Infinity) || 0;

        dispatchAddUserToCourses({
          type: 'update',
          payload: {
            courseId,
            data: { phasePropOverride: [], startTime: newStartTime },
          },
        });
      }
    }
  };

  const handleChangeGroup = (item: IInitialState, groupId: string) => {
    const coursePhaseProps =
      pdpState.classes?.[item.courseId].properties.phaseProps;
    const overrides = item.phasePropOverride || [];

    const effectiveDates =
      coursePhaseProps
        ?.map((phase) => {
          const override = overrides.find(
            (o) =>
              o.phaseTagId === phase.phaseTagId &&
              o.phaseStageId === phase.phaseStageId
          );
          return override?.startDate ?? phase.phaseStartDates?.[groupId];
        })
        .filter((date) => date !== null && date !== undefined) || [];

    const newStartTime =
      effectiveDates.length > 0 ? Math.min(...effectiveDates) : item.startTime;

    dispatchAddUserToCourses({
      type: 'update',
      payload: {
        courseId: item.courseId,
        data: {
          courseGroupId: groupId,
          startTime: newStartTime,
        },
      },
    });
  };

  return (
    <Modal
      title={messages.addMemberToCourses}
      isActive={isOpen}
      onClose={() => {
        onClose();
        setSelectedUserToCourse(undefined);
      }}
    >
      <ModalContainer>
        <div className="flex align-center justify-between flex-1 gap-1">
          <Select
            options={termUserOptions}
            values={termUserOptions.filter(
              (option) => option.value === selectedUserToCourse?.value
            )}
            onChangeValues={(options) =>
              setSelectedUserToCourse(options.length ? options?.[0] : undefined)
            }
            label={messages.selectUser}
            placeholder={messages.selectUser}
            containerStyle={{ width: '50%' }}
          />
          <div className="flex gap-2">
            {Object.keys(tagOptions).map((it) => (
              <Select
                key={it}
                onChangeValues={(values) => {
                  const newTagIds = filterTagIds.filter(
                    (tagId) => tags[tagId].parentId !== Number(it)
                  );
                  if (values.length) newTagIds.push(Number(values[0].value));
                  setFilterTagIds(newTagIds);
                }}
                options={tagOptions[it]}
                values={filterTagIds
                  .filter((tagId) => tags[tagId].parentId === Number(it))
                  .map((it) => ({
                    label: tags[it].text,
                    value: it,
                  }))}
                placeholder={t('all')}
                label={tags[it]?.text}
              />
            ))}
          </div>
          <MultiDatePicker
            label={messages.setGlobalStartDate || 'Set Global Start Date'}
            id="global-start-date-picker"
            setDate={(value) => {
              const newDate = Number(value);
              setGlobalStartDate(newDate);
              addUserToCourses.forEach((item) =>
                updateItemStartTime(item, newDate)
              );
            }}
            value={globalStartDate || undefined}
            hasTime={true}
          />
        </div>
        {selectedUserToCourse && (
          <>
            <Table className="flex flex-col gap-2">
              <TR isHeadRow={true}>
                <TH>{messages.courseTitle}</TH>
                <TH isStatus={true}>{messages.courseStatus}</TH>
                <TH>{messages.selectGroup}</TH>
                <TH>{messages.coursePeriod}</TH>
              </TR>
              {(() => {
                const filteredCourses = addUserToCourses.filter((item) => {
                  const topic = Object.values(pdpState.topics || {}).find(
                    (t) => {
                      const classTopic = classesInTerm.find(
                        (ct) =>
                          ct.topicId === Number(t.id) && ct.id === item.courseId
                      );
                      return classTopic;
                    }
                  );
                  if (!topic) return true;
                  return !filterTagIds.find(
                    (tagId) => !topic.properties.tags.includes(tagId)
                  );
                });

                const displayedCourses = filteredCourses.sort(
                  (a: IInitialState, b: IInitialState) =>
                    Number(b.isMandatoryCourse) - Number(a.isMandatoryCourse)
                );

                return displayedCourses.map((item: IInitialState) => {
                  const participant =
                    pdpState.classes?.[item.courseId]?.participants?.[
                      selectedUserToCourse!.value
                    ];
                  const status = participant?.participantProps?.status;
                  return (
                    <TR
                      key={item.courseId}
                      className="flex justify-between items-center"
                    >
                      <TD>
                        <div
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <h5 style={{ fontWeight: 'bold' }}>
                            {item.courseTitle}
                          </h5>
                          <div>
                            {item.isMandatoryCourse
                              ? messages.mandatoryCourse
                              : messages.optionalCourse}
                          </div>
                          <div
                            style={{
                              color:
                                status === 'Approved'
                                  ? green
                                  : status === 'Rejected'
                                    ? red
                                    : status === 'Dropped'
                                      ? gray
                                      : black,
                            }}
                          >
                            {`${messages.participantStatus} : ${messages.getParticipantStatus(status)}`}
                          </div>
                        </div>
                      </TD>
                      <TD isStatus={true}>
                        <Input
                          id={`${item.courseId}-courseStatus`}
                          type="checkbox"
                          checked={item.isActiveCourse}
                          disabled={status && status !== 'Dropped'}
                          onChange={(event: any) => {
                            dispatchAddUserToCourses({
                              type: 'update',
                              payload: {
                                courseId: item.courseId,
                                data: { isActiveCourse: event.target.checked },
                              },
                            });
                          }}
                        />
                      </TD>
                      <TD>
                        {(() => {
                          const groupOption: IOption[] =
                            pdpState.classes?.[
                              item.courseId
                            ]?.properties.classGroups.map((group) => ({
                              label: group.title,
                              value: group.key,
                            })) ?? [];

                          if (
                            groupOption.length === 1 &&
                            !item.startTime &&
                            !item.courseGroupId
                          ) {
                            handleChangeGroup(
                              item,
                              groupOption[0]?.value.toString() || ''
                            );
                          }

                          return (
                            <Select
                              options={groupOption}
                              values={
                                groupOption.length === 1
                                  ? groupOption
                                  : groupOption.filter(
                                      (option) =>
                                        option.value === item.courseGroupId
                                    )
                              }
                              onChangeValues={(options: IOption[]) => {
                                handleChangeGroup(
                                  item,
                                  options[0]?.value.toString() || ''
                                );
                              }}
                              disabled={groupOption?.length < 2}
                            />
                          );
                        })()}
                      </TD>
                      <TD>
                        {item.isActiveCourse && item.courseGroupId && (
                          <div className="flex flex-col flex-2">
                            <div className="flex justify-between items-center gap-5">
                              <MultiDatePicker
                                label={messages.courseBaseTime}
                                className="flex-1"
                                id={`${item.courseId} - periodPicker - start`}
                                setDate={(value) =>
                                  updateItemStartTime(item, Number(value))
                                }
                                value={Number(item.startTime) ?? null}
                                hasTime={true}
                              />
                              <Button
                                color={'black-outline'}
                                onClick={() =>
                                  handleResetCustomTime(item.courseId)
                                }
                                loading={item.resetLoading}
                              >
                                <Icon type={'rotate-cw'} />
                                <div className="mx-1">
                                  {messages.resetCustomTime}
                                </div>
                              </Button>
                            </div>
                            <CoursePhaseList
                              tags={tags}
                              classPhaseProps={
                                pdpState.classes![item.courseId].properties
                                  .phaseProps!
                              }
                              groupId={item.courseGroupId}
                              onPhaseChange={(overrides) => {
                                const coursePhaseProps =
                                  pdpState.classes![item.courseId].properties
                                    .phaseProps;
                                const effectiveDates =
                                  coursePhaseProps
                                    ?.map((phase) => {
                                      const override = overrides?.find(
                                        (o) =>
                                          o.phaseTagId === phase.phaseTagId &&
                                          o.phaseStageId === phase.phaseStageId
                                      );
                                      return (
                                        override?.startDate ??
                                        phase.phaseStartDates?.[
                                          item.courseGroupId
                                        ]
                                      );
                                    })
                                    .filter(
                                      (date) =>
                                        date !== null && date !== undefined
                                    ) || [];

                                const newStartTime =
                                  effectiveDates.length > 0
                                    ? Math.min(...effectiveDates)
                                    : item.startTime;

                                dispatchAddUserToCourses({
                                  type: 'update',
                                  payload: {
                                    courseId: item.courseId,
                                    data: {
                                      phasePropOverride: overrides,
                                      startTime: newStartTime,
                                    },
                                  },
                                });
                              }}
                              phasePropOverride={item.phasePropOverride}
                            />
                          </div>
                        )}
                      </TD>
                    </TR>
                  );
                });
              })()}
            </Table>
            {filterTagIds.length > 0 &&
              (() => {
                const activeCourses = addUserToCourses.filter(
                  (item) => item.isActiveCourse
                );
                const filteredActiveCourses = activeCourses.filter((item) => {
                  const topic = Object.values(pdpState.topics || {}).find(
                    (t) => {
                      const classTopic = classesInTerm.find(
                        (ct) =>
                          ct.topicId === Number(t.id) && ct.id === item.courseId
                      );
                      return classTopic;
                    }
                  );
                  if (!topic) return true;
                  return !filterTagIds.find(
                    (tagId) => !topic.properties.tags.includes(tagId)
                  );
                });
                const hiddenCount =
                  activeCourses.length - filteredActiveCourses.length;
                return hiddenCount > 0 ? (
                  <div
                    style={{
                      padding: '10px',
                      fontStyle: 'italic',
                      color: 'gray',
                    }}
                  >
                    {t('moreCoursesFiltered', { count: hiddenCount })}
                  </div>
                ) : null;
              })()}
            <div
              style={{
                display: 'flex',
                flex: '1',
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                color={'black-outline'}
                onClick={handleAddAUserToCourses}
                style={{ width: '10rem', marginBottom: '1rem' }}
                loading={loadingAddUserToCourses}
              >
                {messages.save}
              </Button>
            </div>
          </>
        )}
      </ModalContainer>
    </Modal>
  );
};

export default AddUserToCoursesModal;
